import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { adminGet, adminPut, adminPost, adminDel, authHeaders } from "@/lib/adminApi";
import { capFirst, capName } from "@/lib/profile";
import { Volume2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Les deux sons de l'application, remplaçables par n'importe quel fichier audio
const SOUNDS = [
  { key: "sound_ambiance", label: "Bruitage", hint: "Le murmure en boucle sur la première page (vagues par défaut)." },
  { key: "sound_opening", label: "Son de première page", hint: "Le petit son joué à l'ouverture de l'app (clochette de méditation par défaut)." },
];

const FIELDS = [
  { key: "brand_name", label: "Nom de marque" },
  { key: "slogan", label: "Slogan" },
  { key: "support_email", label: "E-mail support" },
  { key: "resalib_url", label: "Lien agenda Resalib (réservation des visios)" },
  { key: "visio_note", label: "Note visio (lien / consigne interne)" },
  { key: "free_weekly_limit", label: "Instants offerts par période (offre Découverte, 0 = illimité)", type: "number" },
];

const PERIODS = ["jour", "semaine", "mois", "trimestre", "année"];

const TOGGLES = [
  { key: "payments_enabled", label: "Activer les paiements en ligne (Stripe — à brancher lors de l'activation)" },
  { key: "visio_payment_required", label: "Exiger le paiement avant la réservation visio (parcours paiement puis Resalib)" },
  { key: "accompaniment_enabled", label: "Activer le module Mon accompagnement (suivi des patientes entre rendez-vous — section 23 du menu admin)" },
];

const KEYS = [
  { key: "email_sender", label: "Adresse e-mail d'expédition (lettre douce, confirmations)", placeholder: "ex. contact@respireaimevibre.com" },
  { key: "resend_api_key", label: "Clé e-mail (Resend)", placeholder: "re_••••••••", secret: true },
  { key: "stripe_public_key", label: "Clé publique Stripe", placeholder: "pk_live_••••••••", secret: true },
  { key: "stripe_secret_key", label: "Clé secrète Stripe", placeholder: "sk_live_••••••••", secret: true },
  { key: "admin_delete_code", label: "Code secret de suppression (exigé pour supprimer un contenu)", placeholder: "••••••", secret: true },
];

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [keys, setKeys] = useState(null);
  const [keyEdits, setKeyEdits] = useState({});
  const [admins, setAdmins] = useState([]);
  const [aEmail, setAEmail] = useState("");
  const [aName, setAName] = useState("");
  const [aPassword, setAPassword] = useState("");
  const [aRole, setARole] = useState("admin");
  const [soundUploading, setSoundUploading] = useState(null);
  const [soundListening, setSoundListening] = useState(null);
  const volumeSave = useRef(null);

  const load = () => {
    adminGet("/settings").then((d) => setSettings(d.settings)).catch(() => {});
    adminGet("/keys").then((d) => setKeys(d.keys)).catch(() => {});
    adminGet("/admins").then((d) => setAdmins(d.admins)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  // Curseur de la vague : enregistrement automatique fiable dès qu'on le bouge
  // (petit délai pour ne pas envoyer une requête à chaque cran)
  const changeWaveVolume = (v) => {
    const next = { ...(settings || {}), wave_volume: v };
    setSettings(next);
    clearTimeout(volumeSave.current);
    volumeSave.current = setTimeout(async () => {
      try {
        await adminPut("/settings", { ...next, free_weekly_limit: Number(next.free_weekly_limit) || 0 });
        toast(`Puissance de la vague : ${v} % — enregistré.`);
      } catch {
        toast("Une erreur est survenue.");
      }
    }, 600);
  };

  if (!settings || !keys) return null;

  const save = async () => {
    try {
      await adminPut("/settings", { ...settings, free_weekly_limit: Number(settings.free_weekly_limit) || 0 });
      toast("Paramètres enregistrés.");
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const saveKeys = async () => {
    const edits = Object.fromEntries(Object.entries(keyEdits).filter(([, v]) => v !== ""));
    if (Object.keys(edits).length === 0) {
      toast("Aucune clé à enregistrer.");
      return;
    }
    try {
      await adminPut("/keys", edits);
      toast("Clés enregistrées — elles restent masquées ici pour leur sécurité.");
      setKeyEdits({});
      load();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  // Remplace un son de l'app par n'importe quel fichier audio (médiathèque)
  const uploadSound = async (key, file) => {
    if (!file) return;
    setSoundUploading(key);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: m } = await axios.post(`${API}/admin/media`, data, { headers: authHeaders() });
      const next = { ...settings, [key]: m.storage_path };
      setSettings(next);
      await adminPut("/settings", { ...next, free_weekly_limit: Number(next.free_weekly_limit) || 0 });
      toast("Son enregistré — il sera joué dès la prochaine visite.");
    } catch (err) {
      toast(err.response?.data?.detail || "Envoi impossible.");
    } finally {
      setSoundUploading(null);
    }
  };

  const resetSound = async (key) => {
    const next = { ...settings, [key]: "" };
    setSettings(next);
    try {
      await adminPut("/settings", { ...next, free_weekly_limit: Number(next.free_weekly_limit) || 0 });
      toast("Son par défaut rétabli.");
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const listenSound = (key) => {
    if (soundListening) {
      soundListening.pause();
      setSoundListening(null);
      return;
    }
    const path = settings[key];
    const src = path ? `${process.env.REACT_APP_BACKEND_URL}/api/media/${path}` : key === "sound_ambiance" ? "/audio/vagues.wav" : "/audio/ouverture.wav";
    const a = new Audio(src);
    a.onended = () => setSoundListening(null);
    a.play().catch(() => toast("Lecture impossible pour le moment."));
    setSoundListening(a);
  };

  const createAdmin = async () => {    if (!aEmail.includes("@") || aPassword.length < 8) {
      toast("Adresse e-mail et mot de passe (8 caractères minimum) sont requis.");
      return;
    }
    try {
      await adminPost("/admins", { email: aEmail, password: aPassword, name: aName, role: aRole });
      toast(`${aRole === "superadmin" ? "Super administrateur" : "Administrateur"} créé.`);
      setAEmail("");
      setAName("");
      setAPassword("");
      load();
    } catch (err) {
      toast(err.response?.data?.detail || "Une erreur est survenue.");
    }
  };

  const removeAdmin = async (a) => {
    try {
      await adminDel(`/admins/${a.id}`);
      toast("Administrateur supprimé.");
      load();
    } catch (err) {
      toast(err.response?.data?.detail || "Une erreur est survenue.");
    }
  };

  return (
    <div data-testid="admin-settings-module">
      <h2 className="rav-admin-h2">Réglages — Identité Respire Aime Vibre</h2>
      <div className="rav-admin-form">
        {FIELDS.map((f) => (
          <label key={f.key} className="rav-field">
            <span>{f.label}</span>
            <input
              className="rav-input"
              data-testid={`settings-${f.key}`}
              type={f.type === "number" ? "number" : "text"}
              value={settings[f.key] ?? ""}
              onChange={(e) => setSettings({ ...settings, [f.key]: ["slogan", "brand_name", "visio_note"].includes(f.key) ? capFirst(e.target.value) : e.target.value })}
            />
          </label>
        ))}
        <label className="rav-field">
          <span>Rythme des Instants offerts</span>
          <select
            className="rav-input"
            data-testid="settings-free_limit_period"
            value={settings.free_limit_period || "semaine"}
            onChange={(e) => setSettings({ ...settings, free_limit_period: e.target.value })}
          >
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                Par {p}
              </option>
            ))}
          </select>
        </label>
        {TOGGLES.map((t) => (
          <label key={t.key} className="rav-field rav-admin-check">
            <input
              type="checkbox"
              data-testid={`settings-${t.key}`}
              checked={settings[t.key] === true}
              onChange={(e) => setSettings({ ...settings, [t.key]: e.target.checked })}
            />
            <span>{t.label}</span>
          </label>
        ))}
        <div className="rav-session-actions">
          <button type="button" className="rav-secondary-button rav-session-btn" data-testid="settings-save" onClick={save}>
            Enregistrer
          </button>
        </div>
      </div>

      <h2 className="rav-admin-h2">Sons de l'application</h2>
      <p className="rav-page-sub rav-admin-note">
        Remplacez les sons d'origine par n'importe quel fichier audio (mp3, m4a, wav, ogg).
      </p>
      <div className="rav-admin-list">
        {SOUNDS.map((s) => (
          <div key={s.key} className="rav-admin-row" data-testid={`settings-${s.key}-row`}>
            <span className="rav-admin-row-label">{s.label}</span>
            <span className="rav-admin-row-sub">
              {s.hint} {settings[s.key] ? "Son personnalisé actif." : "Son par défaut actif."}
            </span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`settings-${s.key}-listen`} onClick={() => listenSound(s.key)}>
                <Volume2 size={13} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                {soundListening ? "Stop" : "Écouter"}
              </button>
              <label className="rav-text-link" data-testid={`settings-${s.key}-upload`} style={{ cursor: "pointer" }}>
                {soundUploading === s.key ? "Envoi…" : "Choisir un son"}
                <input
                  type="file"
                  accept="audio/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    uploadSound(s.key, e.target.files[0]);
                    e.target.value = "";
                  }}
                />
              </label>
              {settings[s.key] && (
                <button type="button" className="rav-text-link" data-testid={`settings-${s.key}-reset`} onClick={() => resetSound(s.key)}>
                  Son par défaut
                </button>
              )}
            </span>
          </div>
        ))}
      </div>

      <div className="rav-admin-row" data-testid="settings-wave-volume-row">
        <span className="rav-admin-row-label">Puissance de la vague</span>
        <span className="rav-admin-row-sub">
          Le niveau du clapotis sur la première page — actuellement {settings.wave_volume ?? 5} %. De 0 % (silence total) à 100 % (volume intégral). Enregistré au relâché et appliqué dès le retour sur la page d'accueil.
        </span>
        <span className="rav-admin-row-actions">
          <input
            type="range"
            className="rav-volume-range"
            data-testid="settings-wave-volume"
            min="0"
            max="100"
            step="1"
            value={settings.wave_volume ?? 5}
            onChange={(e) => changeWaveVolume(Number(e.target.value))}
          />
        </span>
      </div>

      <h2 className="rav-admin-h2">Clés & intégrations</h2>
      <p className="rav-page-sub rav-admin-note">
        Les clés sont conservées en sécurité côté serveur et ne s'affichent jamais en entier. Écrivez une nouvelle valeur
        pour la remplacer ; laissez vide pour ne rien changer.
      </p>
      <div className="rav-admin-form" data-testid="keys-form">
        {KEYS.map((k) => (
          <label key={k.key} className="rav-field">
            <span>
              {k.label} —{" "}
              {keys[k.key]?.set ? <strong data-testid={`key-status-${k.key}`}>configurée ({keys[k.key].preview})</strong> : <em data-testid={`key-status-${k.key}`}>non configurée</em>}
            </span>
            <input
              className="rav-input"
              data-testid={`key-${k.key}`}
              type={k.secret ? "password" : "text"}
              autoComplete="off"
              placeholder={k.placeholder}
              value={keyEdits[k.key] || ""}
              onChange={(e) => setKeyEdits({ ...keyEdits, [k.key]: e.target.value })}
            />
          </label>
        ))}
        <div className="rav-session-actions">
          <button type="button" className="rav-secondary-button rav-session-btn" data-testid="keys-save" onClick={saveKeys}>
            Enregistrer les clés
          </button>
        </div>
      </div>

      <h2 className="rav-admin-h2">Administrateurs</h2>
      <div className="rav-admin-list">
        {admins.map((a, i) => (
          <div key={a.id} className="rav-admin-row" data-testid={`admin-row-${i}`}>
            <span className="rav-admin-row-label">
              {a.name || a.email} — {a.role === "superadmin" ? "Super administrateur" : "Administrateur"}
            </span>
            <span className="rav-admin-row-sub">{a.email}</span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`admin-delete-${i}`} onClick={() => removeAdmin(a)}>
                Supprimer
              </button>
            </span>
          </div>
        ))}
      </div>
      <div className="rav-admin-form" data-testid="admin-create-form">
        <div className="rav-admin-form-row">
          <input
            className="rav-input"
            data-testid="admin-create-name"
            placeholder="Prénom et nom"
            value={aName}
            onChange={(e) => setAName(capName(e.target.value))}
          />
          <select className="rav-input rav-admin-select" data-testid="admin-create-role" value={aRole} onChange={(e) => setARole(e.target.value)}>
            <option value="admin">Administrateur</option>
            <option value="superadmin">Super administrateur</option>
          </select>
        </div>
        <input
          className="rav-input"
          data-testid="admin-create-email"
          type="email"
          placeholder="Adresse e-mail"
          value={aEmail}
          onChange={(e) => setAEmail(e.target.value.toLowerCase())}
        />
        <input
          className="rav-input"
          data-testid="admin-create-password"
          type="password"
          autoComplete="new-password"
          placeholder="Mot de passe (8 caractères minimum)"
          value={aPassword}
          onChange={(e) => setAPassword(e.target.value)}
        />
        <div className="rav-session-actions">
          <button type="button" className="rav-secondary-button rav-session-btn" data-testid="admin-create-submit" onClick={createAdmin}>
            Créer l'administrateur
          </button>
        </div>
      </div>
    </div>
  );
}
